import {
  AutocompleteInput,
  Breadcrumb,
  DateInput,
  Edit,
  NumberInput,
  ReferenceInput,
  SimpleForm,
  TextInput,
} from "@/components/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Landmark, Receipt } from "lucide-react";
import { required, useRecordContext } from "ra-core";
import { Link } from "react-router-dom";
import { DocumentItemsTable } from "../shared/DocumentItemsTable";
import { QuotationMarkupField } from "./QuotationMarkup";
import { GrandTotalsWatcher } from "../shared/GrandTotalsWatcher";
import { RevisedReferenceWatcher } from "../shared/RevisedReferenceWatcher";
import { StatusSelectInput } from "../shared/StatusSelectInput";
import { TermsTemplateSelector } from "../shared/TermsTemplateSelector";
import { requireNonZeroTotal } from "../shared/validators";
import { FilteredContactInput } from "../../misc/FilteredContactInput";

const QuotationEditTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <span className="text-xl font-bold tracking-tight">
      Edit Quotation {record.reference}
    </span>
  );
};

const QuotationEditBreadcrumb = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <Breadcrumb>
      <Breadcrumb.Item><Link to="/quotations">Quotations</Link></Breadcrumb.Item>
      <Breadcrumb.Item><Link to={`/quotations/${record.id}/show`}>{record.reference}</Link></Breadcrumb.Item>
      <Breadcrumb.PageItem>Edit</Breadcrumb.PageItem>
    </Breadcrumb>
  );
};

export const QuotationEdit = () => (
  <Edit disableBreadcrumb title={<QuotationEditTitle />} mutationMode="pessimistic">
    <QuotationEditBreadcrumb />
    <SimpleForm className="max-w-5xl mx-auto space-y-6" validate={requireNonZeroTotal}>
      <Card className="w-full shadow-sm">
        <CardHeader className="pb-3 border-b bg-muted/50">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg font-semibold tracking-tight">
              Quotation Details
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <TextInput
              source="reference"
              label="Quotation No."
              disabled
              className="w-full opacity-70"
            />
            <StatusSelectInput
              source="status"
              label="Status"
              className="w-full"
              choices={[
                { id: "draft", name: "Draft" },
                { id: "sent", name: "Sent" },
                { id: "pending_approval", name: "Pending Approval" },
                { id: "approved", name: "Approved" },
                { id: "rejected", name: "Rejected" },
                { id: "revised", name: "Revised" },
              ]}
            />
            <DateInput
              source="date"
              label="Date"
              validate={required()}
              className="w-full"
            />
            <DateInput
              source="valid_until"
              label="Valid Until"
              className="w-full"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
            <ReferenceInput source="company_id" reference="companies">
              <AutocompleteInput
                label="Company"
                validate={required()}
                className="w-full"
              />
            </ReferenceInput>
            <FilteredContactInput source="customer_id" label="Customer Contact" className="w-full" />
            <TextInput
              source="customer_name"
              label="Name Override"
              validate={required()}
              helperText="Required for record - usually auto-filled from contact/company"
              className="w-full"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-dashed">
            <TextInput
              source="billing_address"
              label="Billing Address"
              multiline
              rows={3}
              helperText="Auto-filled from company; edit to override"
              className="w-full"
            />
            <TextInput
              source="shipping_address"
              label="Shipping Address"
              multiline
              rows={3}
              helperText="Auto-filled from company; edit to override"
              className="w-full"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="w-full shadow-sm">
        <CardHeader className="pb-3 border-b">
          <div className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg font-semibold tracking-tight">
              Line Items & Financials
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          <QuotationMarkupField />
          <DocumentItemsTable showCost showSubItems />
          <GrandTotalsWatcher />
          <RevisedReferenceWatcher />

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 bg-muted p-4 rounded-lg border">
            <NumberInput
              source="cgst_amount"
              label="CGST (₹)"
              readOnly
              className="w-full"
            />
            <NumberInput
              source="sgst_amount"
              label="SGST (₹)"
              readOnly
              className="w-full"
            />
            <NumberInput
              source="igst_amount"
              label="IGST (₹)"
              helperText="Enter manually for interstate"
              className="w-full"
            />
            <NumberInput
              source="total_amount"
              label="Grand Total (₹)"
              readOnly
              className="w-full font-bold text-primary"
            />
            <div className="md:col-span-4">
              <TextInput
                source="amount_in_words"
                label="Amount in Words"
                className="w-full italic"
                readOnly
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="w-full shadow-sm">
        <CardHeader className="pb-3 border-b bg-muted/50">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg font-semibold tracking-tight">
                Terms & Notes
              </CardTitle>
            </div>
            <TermsTemplateSelector type="quotation" />
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <TextInput
            source="terms_conditions"
            label="Terms & Conditions"
            multiline
            rows={4}
            className="w-full max-w-none"
          />
          <TextInput
            source="notes"
            label="Internal Notes"
            multiline
            rows={2}
            className="w-full max-w-none"
          />
        </CardContent>
      </Card>
    </SimpleForm>
  </Edit>
);
